import { BigNumberish, ethers } from 'ethers'
import { Address, L1ChainId, TokenAddress, Wei } from '../../../types'
import { store } from '../../../store'
import { updateDepositingAction } from '../../../store/deposit/actions'
import { isGasAddress } from '../../../utils/address'
import { starknetERC20Abi, starknetZkLinkAbi } from '../../../abi'
import { bn } from '../../../utils/number'
import { JsonRpcProvider } from '@ethersproject/providers'
import { AccountInterface, Contract, validateAndParseAddress } from 'starknet'
import { NetworkRegistry } from '../../../utils/networkRegistry'
import { DepositResult } from './index'
import { compact } from 'lodash'

async function estimateDepositErc20FeeValue(
  provider: JsonRpcProvider,
  zklinkContract: Address,
  tokenContract: Address,
) {
  if (!zklinkContract || !tokenContract) {
    return bn(0)
  }
  if (isGasAddress(tokenContract)) {
    return bn(0)
  }
  const instance = new ethers.utils.Interface([
    'function fee() view returns (uint64)',
  ])
  const data = instance.encodeFunctionData('fee', [])
  return await provider.call({
    to: zklinkContract,
    data,
  })
}

export async function buildStarknetApproveData(
  account: AccountInterface,
  tokenAddress: Address,
  contractAddress: Address,
  amount: Wei,
) {
  const erc20Contract = new Contract(starknetERC20Abi, tokenAddress, account)

  // Check the account has enough allowance.
  const call = erc20Contract.populate('allowance', [
    account.address,
    contractAddress,
  ])

  const allowance = await erc20Contract.allowance(call.calldata)
  if (bn(allowance) > bn(amount)) {
    return undefined
  }

  // If the account does not have enough allowance, approve the contract.
  // Return the approve data.
  return erc20Contract.populate('approve', [contractAddress, amount])
}

export async function buildStarknetDepositERC20Data(
  account: AccountInterface,
  payload: {
    tokenAddress: Address
    contractAddress: Address
    depositTo: Address
    amount: Wei
    subAccountId: number
    mapping: boolean
  },
) {
  const {
    tokenAddress,
    contractAddress,
    depositTo,
    amount,
    subAccountId,
    mapping,
  } = payload
  const mainContract = new Contract(starknetZkLinkAbi, contractAddress, account)
  return mainContract.populate('depositERC20', [
    tokenAddress,
    amount,
    depositTo,
    subAccountId,
    mapping,
  ])
}

export async function sendDepositFromStarknet(
  account: AccountInterface,
  deposit: {
    chainId: L1ChainId
    from: Address
    subAccountId: number
    depositTo: Address
    token: TokenAddress
    amount: BigNumberish
    mapping: boolean
  },
): Promise<DepositResult> {
  try {
    store.dispatch(updateDepositingAction(true))
    console.log(deposit)
    if (!validateAndParseAddress(deposit.token)) {
      throw new Error('depositToken is invalid address')
    }

    if (!validateAndParseAddress(deposit.depositTo)) {
      throw new Error('depositTo is invalid address')
    }

    const currentNetwork = NetworkRegistry.byL1(deposit.chainId)
    if (!currentNetwork) {
      throw new Error(`Unknown network ${deposit.chainId} on deposit ERC20`)
    }

    const { mainContract } = currentNetwork

    if (!mainContract) {
      throw new Error('Main contract address is invalid')
    }
    const approveCall = await buildStarknetApproveData(
      account,
      deposit.token,
      mainContract,
      deposit.amount.toString(),
    )

    const depositCall = await buildStarknetDepositERC20Data(account, {
      tokenAddress: deposit.token,
      contractAddress: mainContract,
      depositTo: deposit.depositTo,
      amount: deposit.amount.toString(),
      subAccountId: deposit.subAccountId,
      mapping: deposit.mapping,
    })

    const executeQueue = compact([approveCall, depositCall])

    const r = await account.execute(executeQueue)

    // account.waitForTransaction(r.transaction_hash).then(onDepositConfirmation)

    return {
      chainId: deposit.chainId,
      from: deposit.from,
      hash: r.transaction_hash,
    }
  } catch (e) {
    return Promise.reject(e)
  } finally {
    store.dispatch(updateDepositingAction(false))
  }
}
